"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import posthog from "posthog-js";
import consentService from "@/services/consent";

export const CONSENT_STORAGE_KEY = "fnp_cookie_consent";
export const CATEGORIES_STORAGE_KEY = "fnp_cookie_categories";
export const GUEST_ID_STORAGE_KEY = "fnp_guest_id";
export const READABLE_CONSENT_COOKIE = "fnp_consent";
export const READABLE_GUEST_COOKIE = "fnp_guest_id";
export const OPEN_COOKIE_SETTINGS_EVENT = "fnp:open-cookie-settings";

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export type ConsentStatus =
  | "unknown"
  | "accepted"
  | "rejected"
  | "essentials"
  | "custom";

/** Backend-facing choice label, stored in user_audit_logs metadata. */
export type ConsentChoice =
  | "agree_and_proceed"
  | "decline_all"
  | "essentials_only"
  | "custom"
  | "withdrawn"
  | "auto_opt_in";

export interface ConsentCategories {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  status: ConsentStatus;
  categories: ConsentCategories;
  guestId: string | null;
  isBannerVisible: boolean;
  /** True once client boot has read persisted state. */
  ready: boolean;
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
  acceptAll: (userId?: string) => void;
  rejectAll: (userId?: string) => void;
  /** Essentials bundle: strictly necessary + analytics, no marketing. */
  acceptEssentials: (userId?: string) => void;
  savePreferences: (
    categories: Omit<ConsentCategories, "necessary">,
    userId?: string,
  ) => void;
  resetConsent: (userId?: string) => void;
  openBanner: () => void;
  closeBanner: () => void;
  /** Dismiss without choosing: 7-day snooze, no backend call, no stored choice. */
  dismissBanner: () => void;
  /** Bumped whenever settings should open straight into Manage choices. */
  manageSignal: number;
  /** Open the banner with the per-category Manage choices view expanded. */
  openManageChoices: () => void;
  /** Enforce backend settings locally for a signed-in user (see impl). */
  syncSignedInConsent: (userId: string) => Promise<boolean>;
}

const DEFAULT_CATEGORIES: ConsentCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const NOOP_CONTEXT: CookieConsentContextType = {
  status: "unknown",
  categories: DEFAULT_CATEGORIES,
  guestId: null,
  isBannerVisible: false,
  ready: false,
  analyticsAllowed: false,
  marketingAllowed: false,
  acceptAll: () => undefined,
  rejectAll: () => undefined,
  acceptEssentials: () => undefined,
  savePreferences: () => undefined,
  resetConsent: () => undefined,
  openBanner: () => undefined,
  closeBanner: () => undefined,
  dismissBanner: () => undefined,
  manageSignal: 0,
  openManageChoices: () => undefined,
  syncSignedInConsent: () => Promise.resolve(false),
};

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

function readStorage(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — cookie fallback still applies */
  }
}

function removeStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

function readCookie(name: string): string | null {
  try {
    if (typeof document === "undefined") return null;
    const match = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
  } catch {
    return null;
  }
}

function writeReadableCookie(name: string, value: string): void {
  try {
    document.cookie =
      `${name}=${encodeURIComponent(value)}; ` +
      `Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
  } catch {
    /* noop */
  }
}

function ensureGuestId(): string {
  const stored = readStorage(GUEST_ID_STORAGE_KEY) ?? readCookie(READABLE_GUEST_COOKIE);
  if (stored) {
    writeStorage(GUEST_ID_STORAGE_KEY, stored);
    return stored;
  }
  const fresh =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  writeStorage(GUEST_ID_STORAGE_KEY, fresh);
  writeReadableCookie(READABLE_GUEST_COOKIE, fresh);
  return fresh;
}

/** Module-level read for non-React callers (track.ts, analytics guards). */
export function getStoredConsentState(): {
  status: ConsentStatus;
  analytics: boolean;
  marketing: boolean;
} {
  if (typeof window === "undefined")
    return { status: "unknown", analytics: false, marketing: false };
  const raw = readStorage(CONSENT_STORAGE_KEY);
  if (
    raw !== "accepted" &&
    raw !== "rejected" &&
    raw !== "essentials" &&
    raw !== "custom"
  )
    return { status: "unknown", analytics: false, marketing: false };
  // Essentials bundles analytics with the strictly-necessary cookies.
  if (raw === "accepted") return { status: raw, analytics: true, marketing: true };
  if (raw === "essentials")
    return { status: raw, analytics: true, marketing: false };
  if (raw === "rejected")
    return { status: raw, analytics: false, marketing: false };
  // Remaining case: "custom" — per-category flags from storage.
  const customStatus = raw as ConsentStatus;
  try {
    const cats = JSON.parse(
      readStorage(CATEGORIES_STORAGE_KEY) ?? "{}",
    ) as Partial<ConsentCategories>;
    return {
      status: customStatus,
      analytics: cats.analytics === true,
      marketing: cats.marketing === true,
    };
  } catch {
    return { status: customStatus, analytics: false, marketing: false };
  }
}

export function CookieConsentProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [status, setStatus] = useState<ConsentStatus>("unknown");
  const [categories, setCategories] =
    useState<ConsentCategories>(DEFAULT_CATEGORIES);
  const [guestId, setGuestId] = useState<string | null>(null);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const healedRef = useRef(false);
  const bootedRef = useRef(false);

  // Boot effect is declared below persistChoice (declaration order matters).

  // Heal: if local state says consented but the readable backend mirror is
  // missing (e.g. set under the old short-lived maxAge), re-sync once.
  useEffect(() => {
    if (!ready || healedRef.current) return;
    if (status !== "accepted" && status !== "essentials" && status !== "custom")
      return;
    if (readCookie(READABLE_CONSENT_COOKIE) !== null) return;
    healedRef.current = true;
    const s = getStoredConsentState();
    const allowed = s.analytics || s.marketing;
    void consentService.postConsent({
      guest_id: guestId ?? ensureGuestId(),
      consent: allowed,
      choice: allowed ? "agree_and_proceed" : "custom",
      categories: { analytics: s.analytics, marketing: s.marketing },
    });
  }, [ready, status, guestId]);

  // Footer "Cookie Settings" entry re-opens the banner without imports.
  useEffect(() => {
    const open = () => setIsBannerVisible(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
  }, []);

  const persistChoice = useCallback(
    (
      nextStatus: ConsentStatus,
      nextCategories: ConsentCategories,
      choice: ConsentChoice,
      userId?: string,
      opts?: { sync?: boolean },
    ) => {
      const id = guestId ?? ensureGuestId();
      const wasAnalytics =
        status === "accepted" ||
        (status === "custom" && categories.analytics);
      const nowAnalytics = nextCategories.analytics;
      setGuestId(id);
      // Bail-out updates: returning prev keeps referential stability so the
      // sync effect (which depends on this callback's identity) doesn't
      // re-run in a loop and hide the banner right after it opens.
      setStatus((prev) => (prev === nextStatus ? prev : nextStatus));
      setCategories((prev) =>
        prev.analytics === nextCategories.analytics &&
        prev.marketing === nextCategories.marketing
          ? prev
          : nextCategories,
      );
      writeStorage(CONSENT_STORAGE_KEY, nextStatus);
      writeStorage(
        CATEGORIES_STORAGE_KEY,
        JSON.stringify(nextCategories),
      );
      // Essentials bundles analytics, so only a full decline maps to false.
      // The human-readable distinction lives in `choice`.
      const backendConsent = nextStatus === "rejected" ? false : true;
      writeReadableCookie(
        READABLE_CONSENT_COOKIE,
        backendConsent ? "true" : "false",
      );
      writeReadableCookie(READABLE_GUEST_COOKIE, id);
      try {
        // PostHog is measurement → analytics flag gates opt-in.
        if (nowAnalytics) {
          posthog.opt_in_capturing();
        } else {
          posthog.opt_out_capturing();
        }
      } catch {
        /* noop */
      }
      setIsBannerVisible(false);
      // Sync mode (signed-in enforcement from the backend row): skip the POST
      // (GET /user/cookie_consent already refreshed server cookies) and skip
      // funnel events — this is state alignment, not a new decision.
      if (opts?.sync) return;
      void consentService.postConsent({
        user_id: userId,
        guest_id: id,
        consent: backendConsent,
        choice,
        categories: {
          analytics: nextCategories.analytics,
          marketing: nextCategories.marketing,
        },
      });
      // Late-consent pageview: instrumentation captures at load only when
      // already consented, and RouteTracker skips first render — so the
      // analytics opt-in transition retro-fires one pageview here.
      // Consent-decided funnel event (recordable once analytics is on; other
      // choices live in the backend audit row as the legal record).
      if (nowAnalytics) {
        try {
          if (!wasAnalytics) {
            posthog.capture("$pageview", {
              $current_url: window.location.href,
            });
          }
          posthog.capture("cookie_consent_decided", {
            choice,
            analytics: nextCategories.analytics,
            marketing: nextCategories.marketing,
            guest_id: id,
          });
        } catch {
          /* noop */
        }
      }
    },
    [guestId, status, categories],
  );

  const acceptAll = useCallback(
    (userId?: string) =>
      persistChoice(
        "accepted",
        { necessary: true, analytics: true, marketing: true },
        "agree_and_proceed",
        userId,
      ),
    [persistChoice],
  );

  const rejectAll = useCallback(
    (userId?: string) =>
      persistChoice(
        "rejected",
        { necessary: true, analytics: false, marketing: false },
        "decline_all",
        userId,
      ),
    [persistChoice],
  );

  const acceptEssentials = useCallback(
    (userId?: string) =>
      persistChoice(
        "essentials",
        { necessary: true, analytics: true, marketing: false },
        "essentials_only",
        userId,
      ),
    [persistChoice],
  );

  const savePreferences = useCallback(
    (
      cats: Omit<ConsentCategories, "necessary">,
      userId?: string,
    ) =>
      persistChoice(
        "custom",
        { necessary: true, analytics: cats.analytics, marketing: cats.marketing },
        "custom",
        userId,
      ),
    [persistChoice],
  );

  /**
   * Signed-in source of truth: fetch persisted settings from the backend and
   * enforce them locally. No backend record (fresh signup, withdrawn) →
   * auto opt-in with the user id. Guests never call this — local state only.
   * Returns true when local state is now consistent with the backend.
   */
  const syncSignedInConsent = useCallback(
    async (userId: string): Promise<boolean> => {
      const res = await consentService.getConsent();
      if (!res.ok || !res.settings) return false;
      const s = res.settings;
      if (s.consent === null || s.consent === undefined) {
        if (s.choice === "withdrawn") {
          // Durable opt-out: enforce locally, do NOT auto opt back in.
          persistChoice(
            "rejected",
            { necessary: true, analytics: false, marketing: false },
            "withdrawn",
            userId,
            { sync: true },
          );
          return true;
        }
        // No record (fresh signup): auto opt-in with the user id.
        persistChoice(
          "accepted",
          { necessary: true, analytics: true, marketing: true },
          "auto_opt_in",
          userId,
        );
        return true;
      }
      if (s.consent === true) {
        if (s.choice === "essentials_only") {
          persistChoice(
            "essentials",
            { necessary: true, analytics: true, marketing: false },
            "essentials_only",
            userId,
            { sync: true },
          );
        } else if (
          s.choice === "custom" &&
          s.categories &&
          typeof s.categories.analytics === "boolean"
        ) {
          persistChoice(
            "custom",
            {
              necessary: true,
              analytics: s.categories.analytics,
              marketing: s.categories.marketing === true,
            },
            "custom",
            userId,
            { sync: true },
          );
        } else {
          persistChoice(
            "accepted",
            { necessary: true, analytics: true, marketing: true },
            (s.choice as ConsentChoice) ?? "agree_and_proceed",
            userId,
            { sync: true },
          );
        }
      } else {
        persistChoice(
          "rejected",
          { necessary: true, analytics: false, marketing: false },
          "decline_all",
          userId,
          { sync: true },
        );
      }
      return true;
    },
    [persistChoice],
  );

  // Boot: restore persisted state, ensure guest id. First visits auto opt-in
  // (choice "auto_opt_in"); the banner is on-demand afterwards — users update
  // choices or opt out from Cookie Settings / the policy page.
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    const id = ensureGuestId();
    setGuestId(id);

    const stored = readStorage(CONSENT_STORAGE_KEY);
    if (
      stored === "accepted" ||
      stored === "rejected" ||
      stored === "essentials" ||
      stored === "custom"
    ) {
      setStatus(stored);
      try {
        const cats = JSON.parse(
          readStorage(CATEGORIES_STORAGE_KEY) ?? "{}",
        ) as Partial<ConsentCategories>;
        if (stored === "accepted") {
          setCategories({ necessary: true, analytics: true, marketing: true });
        } else if (stored === "rejected") {
          setCategories({
            necessary: true,
            analytics: false,
            marketing: false,
          });
        } else if (stored === "essentials") {
          // Essentials = strictly necessary + analytics (no marketing).
          setCategories({
            necessary: true,
            analytics: true,
            marketing: false,
          });
        } else {
          setCategories({
            necessary: true,
            analytics: cats.analytics === true,
            marketing: cats.marketing === true,
          });
        }
      } catch {
        /* keep defaults */
      }
      try {
        // PostHog is measurement → analytics flag (marketing-only stays out).
        // Essentials bundles analytics, so it opts in.
        const s = getStoredConsentState();
        if (s.analytics) posthog.opt_in_capturing();
        else posthog.opt_out_capturing();
      } catch {
        /* posthog not initialised — instrumentation-client handles default */
      }
      setIsBannerVisible(false);
    } else {
      // No stored choice: auto opt-in silently (no banner on first visit).
      persistChoice(
        "accepted",
        { necessary: true, analytics: true, marketing: true },
        "auto_opt_in",
      );
    }
    setReady(true);
    return undefined;
  }, [persistChoice]);

  const resetConsent = useCallback((userId?: string) => {
    removeStorage(CONSENT_STORAGE_KEY);
    removeStorage(CATEGORIES_STORAGE_KEY);
    setStatus("unknown");
    setCategories(DEFAULT_CATEGORIES);
    setIsBannerVisible(true);
    try {
      posthog.opt_out_capturing();
    } catch {
      /* noop */
    }
    // Include the user id when signed in so the backend row is cleared too —
    // otherwise the next sync would resurrect the old settings.
    void consentService.postConsent({
      user_id: userId,
      guest_id: guestId ?? ensureGuestId(),
      consent: null,
      choice: "withdrawn",
    });
  }, [guestId]);

  const openBanner = useCallback(() => setIsBannerVisible(true), []);
  const closeBanner = useCallback(() => setIsBannerVisible(false), []);

  const dismissBanner = useCallback(() => {
    // With auto opt-in there is no stored "unknown" state to snooze against;
    // dismiss simply hides the on-demand banner.
    setIsBannerVisible(false);
  }, []);

  const [manageSignal, setManageSignal] = useState(0);
  const openManageChoices = useCallback(() => {
    setManageSignal((s) => s + 1);
    setIsBannerVisible(true);
  }, []);

  const value = useMemo<CookieConsentContextType>(
    () => ({
      status,
      categories,
      guestId,
      isBannerVisible,
      ready,
      // Derived from the stored categories so bundles stay consistent:
      // accepted → both true; essentials → analytics only; custom → as saved.
      analyticsAllowed: categories.analytics,
      marketingAllowed: categories.marketing,
      acceptAll,
      rejectAll,
      acceptEssentials,
      savePreferences,
      resetConsent,
      openBanner,
      closeBanner,
      dismissBanner,
      manageSignal,
      openManageChoices,
      syncSignedInConsent,
    }),
    [
      status,
      categories,
      guestId,
      isBannerVisible,
      ready,
      acceptAll,
      rejectAll,
      acceptEssentials,
      savePreferences,
      resetConsent,
      openBanner,
      closeBanner,
      dismissBanner,
      manageSignal,
      openManageChoices,
      syncSignedInConsent,
    ],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextType {
  const context = useContext(CookieConsentContext);
  if (!context) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "useCookieConsent called outside CookieConsentProvider; consent actions will be dropped.",
      );
    }
    return NOOP_CONTEXT;
  }
  return context;
}
